import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import {
  capitalGoodsKeys,
  organizationQuestionnaireKeys,
  useAddCapitalGoodsItems,
  useDeleteCapitalGoodsItem,
  useDownloadCapitalGoodsTemplate,
  useGetCapitalGoodsItems,
  useGetLocations,
  useImportCapitalGoodsItems,
  useUpdateCapitalGoods,
  useUpdateCapitalGoodsItem
} from "../../../../../../hooks/query";
import CategoryForm from "./CategoryForm";
import {
  showErrorToast,
  showSuccessToast
} from "../../../../../../utils";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { setAction } from "../../../../../../context/Store/slices/questionnaireSlice";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { CategoryGrid } from "../CategoryGrid";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { PurchasedAndCapitalGoodsModalWrapper } from "../CategoryModalWrappers";
import { withDimensionItems } from "./helpers";
import { targetForCategory } from "../../../../aiIngestTarget";

const FORM_INPUT_KEY = "capitalGoods";

function CapitalGoods({
                        defaultValues,
                        tabKey,
                        handleCategoryChange,
                        finalize,
                        finalizeQuestionnaire,
                        setFinalize,
                        declineReturnToListRequest,
                        isPreview,
                        organizationStructure,
                        dimensionItems
                      }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope3"
  });

  const {
    data: locations
  } = useGetLocations({
    onError: err => handleErrors(err)
  });

  const {
    id: questionnaireId,
    categories: {
      capitalGoods: {
        items
      } = {
        id: null,
        items: []
      }
    }
  } = defaultValues;

  const colHeaders = [
    t(`inputs.${tabKey}.supplierName`),
    t(`inputs.${tabKey}.location`),
    t(`inputs.${tabKey}.productCode`),
    t(`inputs.${tabKey}.itemDescription`),
    t(`inputs.${tabKey}.spentAmount`)
  ];
  const attributesOrder = ["supplierName", "location", "productCategory", "itemDescription", "spentMoneyAmount"];

  const [categoryModalData, setCategoryModalData] = useState(null);

  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const [isDirty, setIsDirty] = useState(false);

  const {
    handleSubmit,
    formState
  } = useForm();

  const prepareSectionValues = useCallback(items => {
    return items.map(item => {
      return {
        id: item.id ?? null,
        dimensionItems: item.dimensionItems ?? null,
        dimensionOneItem: item.dimensionItems?.dimensionOneItem ?? null,
        dimensionTwoItem: item.dimensionItems?.dimensionTwoItem ?? null,
        dimensionThreeItem: item.dimensionItems?.dimensionThreeItem ?? null,
        dimensionFourItem: item.dimensionItems?.dimensionFourItem ?? null,
        dimensionFiveItem: item.dimensionItems?.dimensionFiveItem ?? null,
        location: locations?.find(el => el.id === item.locationId)?.location ?? "",
        locationId: item.locationId,
        productCategory: `${item.productCode} - ${item.productCategory}`,
        productCategoryId: item.productCategoryId ?? null,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount
      };
    });
  }, [locations]);

  const handleAddItem = () => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t(`inputs.capitalGoods.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const handleEditItem = (item) => {
    setCategoryModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        dimensionItems: item.dimensionItems ?? null,
        productCategory: item.productCategory,
        productCategoryId: item.productCategoryId,
        supplierName: item.supplierName,
        itemDescription: item.itemDescription,
        spentMoneyAmount: item.spentMoneyAmount,
        locationId: item.locationId,
        location: item.location
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t(`inputs.capitalGoods.itemsType`),
      organizationStructure,
      dimensionItems
    });
    onCategoryModalOpen();
  };

  const [selectedItem, setSelectedItem] = useState(null);

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id });
  };

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      // If there is a validation error we decline all previously requested actions like switching to different category,
      // finalisation of the questionnaire or 'Return to list'
      dispatch(setAction(""));
      if (finalize) {
        setFinalize(false);
      }
      if (declineReturnToListRequest) {
        declineReturnToListRequest();
      }
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, isDirty, declineReturnToListRequest, finalize, setFinalize, dispatch]);

  const queryConfig = {
    onSuccess: () => {
      showSuccessToast(toast, "Category Capital Goods saved!");
      if (finalize) {
        finalizeQuestionnaire(questionnaireId);
      } else {
        queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleCategoryChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message)
  };

  const formId = `formStep${tabKey}`;
  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateCapitalGoods(queryConfig);

  const { mutate: deleteItem } = useDeleteCapitalGoodsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: downloadTemplate } = useDownloadCapitalGoodsTemplate({
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsed = err.response?.data
        ? JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)))
        : null;
      showErrorToast(toast, parsed?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: importItems } = useImportCapitalGoodsItems({
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.imported"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleImport = (e) => {
    if (!e.target.files?.[0]) return;
    const formData = new FormData();
    formData.append("file", e.target.files[0]);
    dispatch(showAppSpinner());
    importItems({ questionnaireId, data: formData });
  };

  const onSubmit = ((data) => {
    if (!isPreview) {
      if (isDirty) {
        const requestData = {
          items
        };
        update({ questionnaireId, data: requestData });
      } else {
        finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
      }
    } else {
      handleCategoryChange();
    }
  });

  const handleCategoryModalClose = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const { mutate: addItem } = useAddCapitalGoodsItems({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateCapitalGoodsItem({
    onSuccess: () => {
      handleCategoryModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(capitalGoodsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleCategoryModalSubmit = (formData) => {
    const apiData = formData[categoryModalData.fieldArrayName].map(el => withDimensionItems({ ...el }));

    dispatch(showAppSpinner());
    if (categoryModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData
      });
    }
  };

  return (
    <CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId} marginTop={4}>
      {locations && <CategoryGrid
        questionnaireId={questionnaireId}
        columnHeaders={colHeaders}
        attributesOrder={attributesOrder}
        prepareSectionValues={prepareSectionValues}
        handleAddItem={handleAddItem}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        getItemsFunction={useGetCapitalGoodsItems}
        isPreview={isPreview}
        maxHeight={isPreview ? "240" : "210"}
        organizationStructure={organizationStructure}
        onDownloadTemplate={() => downloadTemplate({ questionnaireId })}
        onImport={handleImport}
        importBtnId={`import-${tabKey}-btn`}
        aiTarget={targetForCategory(tabKey)}
        totalsConfig={{ valueKey: "spentMoneyAmount" }}
      />}

      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />

      {
        isCategoryModalOpen && categoryModalData !== null && (
          <PurchasedAndCapitalGoodsModalWrapper
            tabKey={tabKey}
            modalData={categoryModalData}
            attributesOrder={attributesOrder}
            isOpen={isCategoryModalOpen}
            onClose={handleCategoryModalClose}
            onSubmit={handleCategoryModalSubmit}
            locations={locations}
          />
        )
      }
    </CategoryForm>
  );
}

export default CapitalGoods;
